
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AIWorkoutDialog } from "./AIWorkoutDialog";
import { WorkoutDayCard } from "./WorkoutDayCard";
import { useWorkoutForm } from "./hooks/useWorkoutForm";
import { BasicInfoFields } from "./form/BasicInfoFields";
import { WorkoutFormActions } from "./form/WorkoutFormActions";
import { workoutSchema, WorkoutFormValues } from "./schema/workoutSchema";
import { useTranslations } from "@/hooks/useTranslations";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";

export function WorkoutForm() {
  const { t } = useTranslations("workouts");
  const { hasFeature } = useSubscriptionFeatures();
  const canUseAI = hasFeature('ai_workouts');
  
  const form = useForm<WorkoutFormValues>({
    resolver: zodResolver(workoutSchema),
    defaultValues: {
      title: "",
      description: "",
      type: "",
      difficulty_level: "",
      duration: 60,
      equipment_needed: "",
      notes: "",
      days: 1,
      target_student_id: "",
      day_titles: [""]
    }
  });

  const {
    formRef,
    workoutDays,
    isAIModalOpen,
    setIsAIModalOpen,
    isGenerating,
    areAllExpanded,
    toggleAllDays,
    addDay,
    moveDay,
    removeDay,
    addExercise,
    removeExercise,
    updateExercise,
    handleMoveExercise,
    updateDayTitle,
    onSubmit,
    generateWithAI,
    students
  } = useWorkoutForm(form);

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)} ref={formRef}>
          <BasicInfoFields form={form} students={students} />

          <div className="space-y-4">
            <WorkoutFormActions 
              areAllExpanded={areAllExpanded}
              toggleAllDays={toggleAllDays}
              onAIDialogOpen={() => setIsAIModalOpen(true)}
              onAddDay={addDay}
              canUseAI={canUseAI}
            />

            <div className="grid gap-6">
              {workoutDays.map((day, dayIndex) => (
                <div key={dayIndex} className="bg-gray-50/80 backdrop-blur-sm p-4 rounded-lg mx-px my-0 px-0 dark:bg-gray-800/50 dark:border dark:border-gray-700">
                  <WorkoutDayCard
                    dayIndex={dayIndex}
                    dayTitle={day.title || `${t("newWorkout.days.title")} ${dayIndex + 1}`}
                    exercises={day.exercises}
                    onAddExercise={addExercise}
                    onRemoveExercise={removeExercise}
                    onUpdateExercise={updateExercise}
                    onMoveExercise={handleMoveExercise}
                    onUpdateDayTitle={updateDayTitle}
                    onRemoveDay={removeDay}
                    onMoveDay={moveDay}
                    isExpanded={areAllExpanded}
                    totalDays={workoutDays.length}
                  />
                </div>
              ))}
              
              <div className="flex justify-center">
                <Button 
                  type="button" 
                  onClick={addDay} 
                  variant="outline" 
                  className="flex items-center gap-2 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  <Plus className="h-4 w-4" />
                  {t("newWorkout.days.addDay")}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <Button type="submit" size="lg">
              <Save className="mr-2 h-5 w-5" />
              {t("newWorkout.saveButton")}
            </Button>
          </div>
        </form>
      </Form>

      <AIWorkoutDialog
        open={isAIModalOpen}
        onOpenChange={setIsAIModalOpen}
        onGenerate={generateWithAI}
        isGenerating={isGenerating}
        type={form.getValues("type")}
        difficulty={form.getValues("difficulty_level")}
        duration={form.getValues("duration")}
      />
    </div>
  );
}
